import { Grid } from '@mui/material';
import { ReactElement, ReactNode } from 'react';
import { QueryActivityPageMetrics, QueryActivityPageMetricsAvg } from '../../../api/schema';
import KPI from '../../../component/kpi/KPI';
import { metricsOf } from './activityWindow';

type ActivitySnapshotProps = {
  metrics?: QueryActivityPageMetrics;
  baseline?: QueryActivityPageMetricsAvg;
  days?: number;
  fetching: boolean;
};

// Direction of travel for a metric where up is bad. Jobs and equipment are
// neutral -- a busy period is not a worse period -- but a boomerang going up
// always is.
const caption = (current: number, average?: number, upIsBad?: boolean): ReactNode => {
  if (average === undefined) return undefined;
  const rounded = Math.round(average);
  const delta = current - average;
  const sign = delta > 0 ? '+' : delta < 0 ? '−' : '';
  const size = Math.abs(delta);
  // A percentage is only informative once the baseline is big enough for it to
  // mean something; below that the absolute difference says more.
  const change = rounded >= 10 ? `${sign}${Math.round((size / rounded) * 100)}%` : `${sign}${Math.round(size)}`;
  const colour = !upIsBad || delta === 0 ? 'text.secondary' : delta > 0 ? 'error.main' : 'success.main';
  return (
    <>
      {`vs ${rounded.toLocaleString()} avg `}
      <span style={{ color: `var(--activity-delta-${colour === 'text.secondary' ? 'neutral' : delta > 0 ? 'bad' : 'good'})` }}>{change}</span>
    </>
  );
};

/**
 * The page's top row: what this window did, each tile carrying what a normal
 * equivalent window does so the number can be read without hunting for context.
 *
 * Equipment is shown per day because billing is per piece out on a site
 * regardless of how long it stays, which makes the count of pieces moved the
 * number that tracks the work -- and a rate is the only way to compare a
 * running week with a finished one.
 */
export const ActivitySnapshot = ({ metrics, baseline, days, fetching }: ActivitySnapshotProps): ReactElement => {
  const m = metricsOf(metrics);

  const tiles: { title: string; amount: number | string; colour: string; caption?: ReactNode; link?: string }[] = [
    {
      title: 'Jobs Opened',
      amount: m.jobs_opened,
      colour: m.jobs_opened ? 'blue' : 'black',
      caption: caption(m.jobs_opened, baseline?.jobs_opened),
      link: '/customers/job-sites',
    },
    {
      title: 'Jobs Closed',
      amount: m.jobs_closed,
      colour: m.jobs_closed ? 'blue' : 'black',
      caption: caption(m.jobs_closed, baseline?.jobs_closed),
    },
    {
      title: 'Equip Out',
      amount: m.equipment_out.toLocaleString(),
      colour: 'black',
      caption: caption(m.equipment_out, baseline?.equipment_out),
    },
    {
      title: 'Equip In',
      amount: m.equipment_in.toLocaleString(),
      colour: 'black',
      caption: caption(m.equipment_in, baseline?.equipment_in),
    },
    {
      title: 'Returns',
      amount: m.boomerang_real,
      colour: 'black',
      caption: caption(m.boomerang_real, baseline?.boomerang_real, true),
    },
    {
      title: 'Cancellations',
      amount: m.boomerang_artifact,
      colour: 'black',
      caption: caption(m.boomerang_artifact, baseline?.boomerang_artifact, true),
    },
  ];

  return (
    <Grid
      container
      spacing={{ xs: 1, md: 4 }}
      sx={{
        mb: 6,
        // The delta colours are set as variables so the caption can stay a
        // plain node while still following the theme.
        '--activity-delta-neutral': (theme) => theme.palette.text.secondary,
        '--activity-delta-bad': (theme) => theme.palette.error.main,
        '--activity-delta-good': (theme) => theme.palette.success.main,
      }}
    >
      {tiles.map((tile) => (
        <Grid item xs={6} sm={4} md={2} key={tile.title}>
          <KPI title={tile.title} fetching={fetching} dense data={[{ amount: tile.amount, color: tile.colour, caption: tile.caption, link: tile.link }]} />
        </Grid>
      ))}
    </Grid>
  );
};

export default ActivitySnapshot;
